import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from 'i18n';
import AuthContext from '../../context/autenticacion/authContext';
import { useInvestmentSale } from '../../hooks/useInvestmentSale';
import InvestmentModal from '../inversiones/components/InvestmentModal';
import { fechaCorta, money, num, transcurrido } from './utils';
import './mercado.css';
import './mis.css';

const Esqueleto = () => (
  <div className="p7x-grid">{[0, 1, 2].map((i) => <div key={i} className="p7x-esq" />)}</div>
);

const Tarjeta = ({ inv, vendida, onVender }) => {
  const { t } = useT();
  const { pct, dias } = transcurrido(inv.date_investment, inv.end_date);
  const enVenta = Number(inv.mercado_secundario) === 1;
  return (
    <article className="p7x-card">
      <div className="p7x-top">
        <div style={{ minWidth: 0 }}>
          <h3>{inv.name}</h3>
          <span className="p7x-ref">{t('inv.ref', { id: inv.id })}</span>
        </div>
        {vendida && <span className="p7-chip p7-chip-ok">{t('inv.vendida')}</span>}
        {!vendida && enVenta && <span className="p7-chip p7-chip-wait">{t('inv.enVenta')}</span>}
      </div>

      <div className="p7x-monto">
        <strong>${money(inv.investment_amount)}</strong>
        <span>{t('inv.invertidos')}</span>
        <span className="p7x-gana" style={{ marginLeft: 'auto' }}>+${money(inv.rendimiento)}</span>
      </div>

      {!vendida && (
        <div className="p7x-tiempo">
          <div className="p7x-tiempo-top">
            <span>{fechaCorta(inv.date_investment)}</span>
            <span>{dias === null ? fechaCorta(inv.end_date) : dias === 0 ? t('inv.venceHoy') : t(dias === 1 ? 'inv.paraCierre1' : 'inv.paraCierre', { n: dias })}</span>
          </div>
          <div className="p7x-linea" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      <dl className="p7x-datos">
        <div><dt>{t('inv.tasa')}</dt><dd>{inv.tax_referential_client ?? inv.fee_total_contract ?? '—'}{inv.tax_referential_client != null || inv.fee_total_contract != null ? '%' : ''}</dd></div>
        <div><dt>{vendida ? t('inv.vendidaPor') : t('inv.saldoTotal')}</dt><dd>${money(vendida ? inv.monto_venta : inv.saldo)}</dd></div>
        <div><dt>{vendida ? t('inv.fechaVenta') : t('inv.cierre')}</dt><dd>{fechaCorta(vendida ? inv.date_sold : inv.end_date)}</dd></div>
      </dl>

      {!vendida && (
        <div className="p7x-acc">
          <button type="button" className={enVenta ? 'p7-btn-ghost' : 'p7-btn-gold'} disabled={enVenta} onClick={() => onVender(inv)}>
            {enVenta ? t('inv.yaEnVenta') : t('inv.ponerVenta')}
          </button>
        </div>
      )}
    </article>
  );
};

const MisInversiones = () => {
  const { t } = useT();
  const { mis_inversiones_activas, mis_inversiones_vendidas, TodosInversiones, TodosInversionesVendidas, TodosOfertas } = useContext(AuthContext);
  const [tab, setTab] = useState('activas');
  const [cargando, setCargando] = useState(true);
  const [sel, setSel] = useState(null);
  const venta = useInvestmentSale();

  useEffect(() => {
    let vivo = true;
    Promise.all([TodosInversiones(), TodosInversionesVendidas()]).finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activas = useMemo(() => mis_inversiones_activas || [], [mis_inversiones_activas]);
  const vendidas = mis_inversiones_vendidas || [];

  const kpi = useMemo(() => activas.reduce((a, i) => ({
    invertido: a.invertido + num(i.investment_amount),
    rend: a.rend + num(i.rendimiento),
    saldo: a.saldo + num(i.saldo),
  }), { invertido: 0, rend: 0, saldo: 0 }), [activas]);

  const lista = tab === 'activas' ? activas : vendidas;

  const cerrar = () => { setSel(null); venta.clearSaleResult(); };
  const enviar = async (datos) => {
    const ok = await venta.submitSale(datos);
    if (ok) {
      TodosInversiones();
      TodosOfertas && TodosOfertas();
      setTimeout(cerrar, 1800);
    }
  };

  return (
    <div className="content">
      <section className="p7x-hero">
        <h2>{t('inv.titulo')}</h2>
        <p>{t('inv.sub')}</p>
        <div className="p7x-kpis">
          <div className="p7x-kpi"><span>{t('inv.kpiInvertido')}</span><strong>${money(kpi.invertido)}</strong></div>
          <div className="p7x-kpi verde"><span>{t('inv.kpiRend')}</span><strong>+${money(kpi.rend)}</strong></div>
          <div className="p7x-kpi gold"><span>{t('inv.kpiSaldo')}</span><strong>${money(kpi.saldo)}</strong></div>
          <div className="p7x-kpi"><span>{t('inv.kpiActivas')}</span><strong>{activas.length}</strong></div>
        </div>
      </section>

      <div className="p7x-bar">
        <div className="p7-seg" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'activas'} className={tab === 'activas' ? 'on' : ''} onClick={() => setTab('activas')}>{t('inv.tabActivas')}<span className="p7x-n">{activas.length}</span></button>
          <button type="button" role="tab" aria-selected={tab === 'vendidas'} className={tab === 'vendidas' ? 'on' : ''} onClick={() => setTab('vendidas')}>{t('inv.tabVendidas')}<span className="p7x-n">{vendidas.length}</span></button>
        </div>
        <Link to="/cliente/projectos" className="p7-btn-ghost" style={{ textDecoration: 'none' }}>{t('inv.verProyectos')}</Link>
      </div>

      {cargando && lista.length === 0 ? <Esqueleto /> : lista.length === 0 ? (
        <div className="p7x-vacio">
          <h3>{tab === 'activas' ? t('inv.vacioActivas') : t('inv.vacioVendidas')}</h3>
          <p>{tab === 'activas' ? t('inv.vacioActivasAyuda') : t('inv.vacioVendidasAyuda')}</p>
          {tab === 'activas' && <Link to="/cliente/projectos" className="p7-btn-gold" style={{ textDecoration: 'none', display: 'inline-block' }}>{t('inv.explorar')}</Link>}
        </div>
      ) : (
        <div className="p7x-grid">
          {lista.map((inv) => <Tarjeta key={inv.id} inv={inv} vendida={tab === 'vendidas'} onVender={setSel} />)}
        </div>
      )}

      <InvestmentModal open={!!sel} investment={sel} onClose={cerrar} onSubmit={enviar} isSubmitting={venta.isSubmitting} saleResult={venta.saleResult} />
    </div>
  );
};

export default MisInversiones;
